// Params Keycloak appends to the redirect URI when it hands the browser back.
// They are consumed by oidc-client-ts and must not survive into the restored
// URL — `state` in particular would be replayed as a stale callback on reload.
const OIDC_CALLBACK_PARAMS = [
  "code",
  "state",
  "session_state",
  "iss",
  "error",
  "error_description",
];

/**
 * Drops the consumed callback params from a `URLSearchParams`, returning a new
 * instance. Exported because they can be resurrected after `onSigninCallback`
 * has cleaned them: `history.replaceState` is invisible to React Router, so
 * anything rebuilding the query string from React Router's snapshot writes them
 * straight back. Callers in that position rebuild from `window.location` and
 * run the result through here.
 */
export const stripOidcCallbackParams = (params) => {
  const next = new URLSearchParams(params);
  for (const param of OIDC_CALLBACK_PARAMS) {
    next.delete(param);
  }
  return next;
};

/**
 * The URL to come back to after a Keycloak round trip.
 *
 * Everything the user deep-linked with is carried across: a share link is only
 * worth sharing if it survives the login it triggers, and the params are where
 * the interesting state lives (which workbook, which saved view, which
 * exploration). Only OIDC's own callback params are dropped.
 *
 * Serves both ends of the trip, which is why the stripping is unconditional:
 * on the way out (`Unauthenticated`) there are no callback params to remove,
 * and on the way back (`onSigninCallback`) they are exactly what has to go.
 *
 * NOTE: the result is sent to Keycloak as `redirect_uri`, so the client's valid
 * redirect URIs must admit a query string — a trailing `/*` pattern does.
 *
 * @param {Location} [location] - defaults to the live browser location.
 * @returns {string} absolute URL, params and fragment intact.
 */
export const buildRedirectUri = (location = window.location) => {
  const base = `${location.protocol}//${location.host}${location.pathname}`;

  const search = stripOidcCallbackParams(
    new URLSearchParams(location.search)
  ).toString();

  // The fragment never reaches the server, but it does survive the redirect and
  // is part of the link the user pasted.
  return `${base}${search ? `?${search}` : ""}${location.hash ?? ""}`;
};
